import { useCallback, useContext, useMemo, useState } from 'react';
import {
  ThumbUp as AcceptIcon,
  CloudDownloadOutlined,
  ThumbDown as DeclineIcon,
  DeleteOutline as DeleteIcon,
  MoreVert,
} from '@mui/icons-material';
import { Box, Button, Chip, IconButton, ListItemText, Menu, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DocumentReferencesView } from 'api';
import { DELETE_REFERENCE_CUSTOM_MESSAGE, FILE_NAME } from 'common/constants/documents';
import * as messages from 'common/constants/messages/validations';
import { ConfirmationDelete } from 'components/Dialogs';
import { DialogContext, LayoutContext } from 'context';
import useDocumentsContext from 'context/DocumentsContext';
import FilesService from 'services/documents';
import { useDocuments, useResponse } from 'services/hooks';
import { Logger } from 'utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { ReferencedDocumentProps } from './ReferencedDocument.props';
import useGetFileIconByExtension from '../../../../utilities/getFileIconByExtension/getFileIconByExtension';
import { PREVIEWABLE_DOC_TYPES } from '../../../Drawer/document-preview-drawer-tab/document-preview.constants';

const ReferencedDocument = ({
  document,
  selectedMeasurementDateId,
  currentPage,
  isDisabled,
  areDocumentsActionsDisabled,
  onClick,
  citationMetadata,
  citationNumber,
}: ReferencedDocumentProps) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const filesService = useMemo(() => new FilesService(), []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [suggestionAnchorEl, setSuggestionAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const suggestionOpen = Boolean(suggestionAnchorEl);
  const { viewOnlyUser } = useContext(LayoutContext);
  const dialogContext = useContext(DialogContext);
  const { documents, updateReferencedDocuments } = useDocumentsContext();
  const { addDocumentReferencesObject, downloadDocument, setIsLoading } = useDocuments();
  const { errorNotification, successNotification } = useResponse();
  
  const isPendingApproval = (document as any).pending_auto_classification_approval;
  const aiClassification = (document as any).ai_classification;
  const isPreviewableFileType = useMemo(
    () => PREVIEWABLE_DOC_TYPES.includes(document.file.file_type),
    [document.file.file_type]
  );
  const currentReference = useMemo(
    () => document.referenced_in?.find(item => item.reference_object_type === currentPage),
    [document.referenced_in, currentPage]
  );

  const getReferencesData = (referencedFeatureId: number) => ({
    create_list: [],
    delete_list: [referencedFeatureId],
  });

  const handleReferences = async (): Promise<void> => {
    try {
      // Get id of the corresponding reference
      const referencedFeatureId = currentReference?.id;
      if (!referencedFeatureId) {
        Logger.error('Referenced feature ID not found', {
          document,
          currentPage,
        });
        return;
      }
      const updatedReferenceData = getReferencesData(referencedFeatureId);
      const requestData = {
        ...updatedReferenceData,
        measurement_date_id: selectedMeasurementDateId,
      };
      await addDocumentReferencesObject(selectedMeasurementDateId, requestData);
      await updateReferencedDocuments(documents, currentPage);
      queryClient.invalidateQueries({
        queryKey: ['documents'],
        exact: false,
      });
      successNotification(messages.REFERENCE_DELETED_SUCCESS);
    } catch (error) {
      errorNotification(messages.REFERENCE_DELETED_ERROR);
      Logger.error('Error deleting reference', { error });
    }
  };

  const handleClick = async (): Promise<void> => {
    const { showDialog } = dialogContext;
    const ContentComponent = (
      <ConfirmationDelete customMessage={DELETE_REFERENCE_CUSTOM_MESSAGE(document)} itemName={FILE_NAME(document)} />
    );

    showDialog(
      getCancelDialogOptions(
        () => ContentComponent,
        () => handleReferences()
      )
    );
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    handleClick();
  };

  const handleDownload = useCallback(
    async (documentParam: DocumentReferencesView) => {
      setIsLoading(true);
      await downloadDocument(documentParam);
      setIsLoading(false);
    },
    [downloadDocument, setIsLoading]
  );

  const handleAcceptClassification = useCallback(async () => {
    try {
      await filesService.approveClassification(String(document.document_reference_id));
      enqueueSnackbar('Classification suggestion accepted', { variant: 'success' });
      updateReferencedDocuments?.(documents);
      queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });
    } catch (error) {
      Logger.error('Error accepting classification', error)
      enqueueSnackbar('Failed to accept classification', { variant: 'error' });
    }
  }, [filesService, document.document_reference_id, enqueueSnackbar, updateReferencedDocuments, documents, queryClient]);

  const handleDeclineClassification = useCallback(async () => {
    try {
      await filesService.declineClassification(String(document.document_reference_id));
      enqueueSnackbar('Classification suggestion dismissed', { variant: 'success' });
      updateReferencedDocuments?.(documents);
      queryClient.invalidateQueries({ queryKey: ['documents'], exact: false });
    } catch (error) {
      Logger.error('Error accepting classification', error)
      enqueueSnackbar('Failed to dismiss classification', { variant: 'error' });
    }
  }, [filesService, document.document_reference_id, enqueueSnackbar, updateReferencedDocuments, documents, queryClient]);

  const handleSuggestionClick = (event: React.MouseEvent<HTMLElement>) => {
    setSuggestionAnchorEl(event.currentTarget);
  };

  const handleSuggestionClose = () => {
    setSuggestionAnchorEl(null);
  };

  const handleDocumentClick = useCallback(() => {
    if (onClick && isPreviewableFileType && currentReference) {
      onClick(currentReference);
    } else {
      handleDownload(document);
    }
  }, [onClick, isPreviewableFileType, document, handleDownload, currentReference]);

  const formatCitationTooltip = () => `Document: ${document.file.filename}.${document.file.file_type}`;

  return (
    <Stack
      sx={{
        width: 'fit-content',
        maxWidth: '100%',
        padding: isPendingApproval ? '0.5rem' : 0,
        border: isPendingApproval ? '2px dashed' : 'none',
        borderColor: isPendingApproval ? 'primary.main' : 'transparent',
        borderRadius: isPendingApproval ? '8px' : 0,
        '&:hover': {
          cursor: 'pointer',
        },
      }}
      direction={'row'}
      spacing={2}
      alignItems="center"
      aria-label="Referenced document">
      {citationNumber !== undefined && (
        <Tooltip
          title={
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{formatCitationTooltip()}</pre>
          }
          placement="top">
          <Chip
            label={`[${citationNumber}]`}
            size="small"
            sx={{
              height: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiChip-label': {
                padding: '0 6px',
              },
            }}
          />
        </Tooltip>
      )}
      {useGetFileIconByExtension(document.file.file_type)}
      <Tooltip
        disableHoverListener={areDocumentsActionsDisabled}
        title={isPreviewableFileType ? 'Click to preview.' : 'Click to download. (preview coming soon)'}
        placement="top-start">
        <ListItemText
          primary={FILE_NAME(document)}
          onClick={areDocumentsActionsDisabled ? undefined : () => handleDocumentClick()}
          sx={{
            '& .MuiListItemText-primary': {
              color: isPendingApproval ? 'primary.main' : 'inherit',
              fontWeight: isPendingApproval ? 500 : 400,
            },
          }}
        />
      </Tooltip>
      
      {isPendingApproval ? (
        /* Pending Classification Approval UI */
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          <IconButton
            disabled={isDisabled || viewOnlyUser}
            onClick={() => handleDownload(document)}
            aria-label="Download"
            size="small">
            <CloudDownloadOutlined fontSize="small" />
          </IconButton>
          <Button
            size="small"
            variant="text"
            startIcon={<AcceptIcon sx={{ fontSize: 14 }} />}
            onClick={handleAcceptClassification}
            disabled={isDisabled || viewOnlyUser}
            sx={{ textTransform: 'none', fontWeight: 500, color: 'primary.main' }}>
            Accept
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<DeclineIcon sx={{ fontSize: 14 }} />}
            onClick={handleDeclineClassification}
            disabled={isDisabled || viewOnlyUser}
            sx={{ textTransform: 'none', fontWeight: 500, color: 'error.main' }}>
            Dismiss
          </Button>
          <Typography
            variant="body2"
            onClick={handleSuggestionClick}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { textDecoration: 'underline' },
            }}>
            Why this <span style={{ color: '#1976d2' }}>suggestion</span>?
          </Typography>
          
          {/* Suggestion Details Popover */}
          <Popover
            open={suggestionOpen}
            anchorEl={suggestionAnchorEl}
            onClose={handleSuggestionClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: { width: 320, p: 2, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
              },
            }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <AcceptIcon sx={{ color: '#1976d2', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Suggested Document
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add <span style={{ color: '#1976d2' }}>{document.file.filename}</span> to{' '}
                  <span style={{ color: '#1976d2' }}>Documents</span>
                </Typography>
              </Box>
            </Stack>

            {aiClassification?.commentary && aiClassification.commentary.length > 0 && (
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} fontStyle="italic">
                  Why this suggestion?
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                  {aiClassification.commentary.map((note: { content: string }, index: number) => (
                    <Typography
                      key={note.content}
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                      {note.content}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-start" mt={2}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  handleSuggestionClose(); handleAcceptClassification(); 
                }}
                startIcon={<AcceptIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 500 }}>
                Accept this Suggestion
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  handleSuggestionClose(); handleDeclineClassification(); 
                }}
                startIcon={<DeclineIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 500, color: 'error.main' }}>
                Dismiss
              </Button>
            </Stack>
          </Popover>
        </Stack>
      ) : (
        /* Normal Document Actions */
        <Stack direction={'row'} spacing={2}>
          <IconButton
            disabled={isDisabled || viewOnlyUser}
            onClick={() => handleDownload(document)}
            aria-label="Download"
            size="small">
            <CloudDownloadOutlined fontSize="small" />
          </IconButton>
          <IconButton
            disabled={isDisabled || viewOnlyUser}
            onClick={handleMenuClick}
            aria-label="More actions"
            size="small">
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            slotProps={{
              list: {
                'aria-labelledby': 'more-actions-button',
              },
            }}>
            <MenuItem onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </Stack>
      )}
    </Stack>
  );
};

export default ReferencedDocument;
